import React from 'react';
import Input from 'formik-antd/lib/input';
import Select from 'formik-antd/lib/select';
import { Switch } from './Switch';
import Form from 'formik-antd/lib/form';
import { TFunction } from 'i18next';
import {
  Flex,
  KeyValue,
  Grid,
  Segment,
  FormGroup,
  RemoteAccessVNCProfile,
  vncValidationSchema,
} from '@mss/mss-component-library';
import { Formik, ErrorMessage } from 'formik';
import emptyProfiles from './emptyProfiles';
import { useTranslation } from 'react-i18next';
import SubmitButton from 'jsx/common/SubmitButton';
import Button from 'jsx/common/Button';
import { Trash } from 'react-feather';
import ToggleEnabled from './ToggleEnabled';
import colordepths from './colordepths';
import { ProfileCollapse } from './ProfileCollapse';

const vncClipboardEncodings = (t: TFunction): KeyValue[] => [
  { value: 'ISO8859-1', key: t('ISO8859-1') },
  { value: 'UTF-8', key: t('UTF-8') },
  { value: 'UTF-16', key: t('UTF-16') },
  { value: 'CP1252', key: t('CP1252') },
];

const vncCursor = (t: TFunction): KeyValue[] => [
  { value: 'local', key: t('Local') },
  { value: 'remote', key: t('Remote') },
];
type VNCCollapseProps = {
  onSubmit: (values: any) => any;
  profile?: RemoteAccessVNCProfile & { id: string };
  asset: string;
  deleteProfile?: (id: string) => void;
  readonly?: boolean;
};

const VNCCollapse = ({
  onSubmit,
  profile,
  asset,
  deleteProfile,
  readonly,
}: VNCCollapseProps) => {
  const [t] = useTranslation(['remoteAccess', 'common', 'asset']);
  const toggleEnabled = () => {
    onSubmit({ ...profile, enabled: !profile?.enabled });
  };
  return (
    <Formik
      validationSchema={vncValidationSchema(t)}
      onSubmit={onSubmit}
      initialValues={
        profile
          ? { ...emptyProfiles.vnc, ...profile, asset }
          : { ...emptyProfiles.vnc, asset }
      }
    >
      {({ values, isSubmitting, errors, setFieldValue, setFieldTouched }) => {
        const hasErrors = Object.keys(errors).length > 0;

        return (
          <ProfileCollapse
            defaultActiveKey={profile ? undefined : 'vnc'}
            bordered={false}
            expandIconPosition="end"
          >
            <ProfileCollapse.Panel
              key="vnc"
              header={
                <span>
                  {t('VNC')} - {values.name}{' '}
                  {!profile && (
                    <strong style={{ color: 'red' }}>{t('Unsaved')}</strong>
                  )}
                </span>
              }
              extra={
                <ToggleEnabled
                  setFieldValue={setFieldValue}
                  t={t}
                  checked={values.enabled}
                  onSubmit={profile ? toggleEnabled : undefined}
                  disabled={readonly}
                />
              }
            >
              <Form>
                <Grid.Container>
                  <Segment
                    heading={
                      <Flex v="center" h={deleteProfile ? 'between' : 'start'}>
                        <span>{t('Connection Settings')}</span>
                        {deleteProfile && profile && (
                          <Button
                            size="small"
                            onClick={() => deleteProfile(profile.id)}
                            data-ta="VNCCollapseDeleteButton"
                          >
                            <Flex v="center">
                              <Trash size={14} className="mr-1" />
                              {t('Delete')}
                            </Flex>
                          </Button>
                        )}
                      </Flex>
                    }
                  >
                    <Grid.Row>
                      <Grid.Col width={4}>
                        <FormGroup
                          error={<ErrorMessage name="name" />}
                          label={t('Name')}
                          required
                          input={
                            <Input
                              disabled={readonly}
                              name="name"
                              value={values.name}
                              onInput={() =>
                                setFieldTouched(`name`, true, true)
                              }
                            />
                          }
                        />
                      </Grid.Col>
                      <Grid.Col width={4}>
                        <FormGroup
                          error={<ErrorMessage name="settings.port" />}
                          label={t('Port')}
                          input={
                            <Input
                              disabled={readonly}
                              name="settings.port"
                              type="number"
                              value={values.settings.port || ''}
                            />
                          }
                        />
                      </Grid.Col>
                    </Grid.Row>
                  </Segment>
                  <Segment heading={t('Authentication Settings')}>
                    <Grid.Row>
                      <Grid.Col width={4}>
                        <FormGroup
                          error={<ErrorMessage name="settings.username" />}
                          label={t('Username')}
                          input={
                            <Input
                              disabled={readonly}
                              name="settings.username"
                              value={values.settings.username}
                            />
                          }
                        />
                      </Grid.Col>
                      <Grid.Col width={4}>
                        <FormGroup
                          error={<ErrorMessage name="settings.password" />}
                          label={t('Password')}
                          input={
                            <Input.Password
                              disabled={readonly}
                              name="settings.password"
                              defaultValue={
                                values.settings_meta.secrets.password
                                  ? '*******************'
                                  : ''
                              }
                              allowClear={true}
                              visibilityToggle={true}
                              onChange={val => {
                                setFieldValue(
                                  'settings.password',
                                  val.target.value
                                );
                              }}
                            />
                          }
                        />
                      </Grid.Col>
                    </Grid.Row>
                  </Segment>
                  <Segment heading={t('Session Recording Settings')}>
                    <Grid.Row>
                      <Grid.Col width={3}>
                        <FormGroup
                          label={t('Record session')}
                          input={<Switch name="record_session" size="small" />}
                        />
                      </Grid.Col>
                      <Grid.Col width={3}>
                        <FormGroup
                          label={t('Exclude output')}
                          input={
                            <Switch
                              name="settings.recording-exclude-output"
                              disabled={!values.record_session}
                              size="small"
                            />
                          }
                        />
                      </Grid.Col>
                      <Grid.Col width={3}>
                        <FormGroup
                          label={t('Exclude mouse')}
                          input={
                            <Switch
                              name="settings.recording-exclude-mouse"
                              disabled={!values.record_session}
                              size="small"
                            />
                          }
                        />
                      </Grid.Col>
                      <Grid.Col width={3}>
                        <FormGroup
                          label={t('Include keys')}
                          input={
                            <Switch
                              name="settings.recording-include-keys"
                              disabled={!values.record_session}
                              size="small"
                            />
                          }
                        />
                      </Grid.Col>
                    </Grid.Row>
                  </Segment>
                  <Segment heading={t('Display')}>
                    <Grid.Row>
                      <Grid.Col width={4}>
                        <FormGroup
                          error={<ErrorMessage name="settings.cursor" />}
                          label={t('Cursor')}
                          input={
                            <Select
                              disabled={readonly}
                              name="settings.cursor"
                              style={{ width: '100%' }}
                              placeholder={`${t('Please select one')}...`}
                            >
                              {vncCursor(t).map((mode: KeyValue) => (
                                <Select.Option
                                  key={mode.key}
                                  value={mode.value}
                                >
                                  {mode.key}
                                </Select.Option>
                              ))}
                            </Select>
                          }
                        />
                      </Grid.Col>
                      <Grid.Col width={4}>
                        <FormGroup
                          error={<ErrorMessage name="settings.color-depth" />}
                          label={t('Color depth')}
                          input={
                            <Select
                              disabled={readonly}
                              name="settings.color-depth"
                              style={{ width: '100%' }}
                              placeholder={`${t('Please select one')}...`}
                            >
                              {colordepths.map(colordepth => (
                                <Select.Option
                                  key={colordepth.key}
                                  value={colordepth.value}
                                >
                                  {colordepth.key}
                                </Select.Option>
                              ))}
                            </Select>
                          }
                        />
                      </Grid.Col>
                      <Grid.Col width={4} />
                      <Grid.Col width={4}>
                        <FormGroup
                          error={<ErrorMessage name="settings.read-only" />}
                          label={t('Read only')}
                          input={
                            <Switch
                              disabled={readonly}
                              name="settings.read-only"
                              size="small"
                            />
                          }
                        />
                      </Grid.Col>
                      <Grid.Col width={4}>
                        <FormGroup
                          error={<ErrorMessage name="settings.swap-red-blue" />}
                          label={t('Swap red/blue components')}
                          input={
                            <Switch
                              disabled={readonly}
                              name="settings.swap-red-blue"
                              size="small"
                            />
                          }
                        />
                      </Grid.Col>
                    </Grid.Row>
                  </Segment>
                  <Segment heading={t('Device redirection')}>
                    <Grid.Row>
                      <Grid.Col width={6}>
                        <FormGroup
                          error={
                            <ErrorMessage name="settings.audio-servername" />
                          }
                          label={t('Audio server name')}
                          input={
                            <Input
                              disabled={readonly}
                              name="settings.audio-servername"
                              value={values.settings['audio-servername'] || ''}
                            />
                          }
                        />
                      </Grid.Col>
                      <Grid.Col width={6}>
                        <FormGroup
                          error={<ErrorMessage name="settings.enable-audio" />}
                          label={t('Enable audio')}
                          input={
                            <Switch
                              disabled={readonly}
                              name="settings.enable-audio"
                              size="small"
                            />
                          }
                        />
                      </Grid.Col>
                    </Grid.Row>
                  </Segment>
                  <Segment heading={t('Clipboard')}>
                    <Grid.Row>
                      {' '}
                      <Grid.Col width={4}>
                        <FormGroup
                          error={
                            <ErrorMessage name="settings.clipboard-encoding" />
                          }
                          label={t('Encoding')}
                          input={
                            <Select
                              disabled={readonly}
                              name="settings.clipboard-encoding"
                              style={{ width: '100%' }}
                              placeholder={`${t('Please select one')}...`}
                            >
                              {vncClipboardEncodings(t).map(
                                (encoding: KeyValue) => (
                                  <Select.Option
                                    key={encoding.key}
                                    value={encoding.value}
                                  >
                                    {encoding.key}
                                  </Select.Option>
                                )
                              )}
                            </Select>
                          }
                        />
                      </Grid.Col>
                      <Grid.Col width={4}>
                        <FormGroup
                          error={<ErrorMessage name="settings.disable-copy" />}
                          label={t('Disable copy from remote')}
                          input={
                            <Switch
                              disabled={readonly}
                              name="settings.disable-copy"
                              size="small"
                            />
                          }
                        />
                      </Grid.Col>
                      <Grid.Col width={4}>
                        <FormGroup
                          error={<ErrorMessage name="settings.disable-paste" />}
                          label={t('Disable paste from client')}
                          input={
                            <Switch
                              disabled={readonly}
                              name="settings.disable-paste"
                              size="small"
                            />
                          }
                        />
                      </Grid.Col>{' '}
                    </Grid.Row>
                  </Segment>

                  <Grid.Row>
                    <Grid.Col width={12} className="mt-4">
                      <Flex h="end" v="center">
                        <SubmitButton
                          loading={isSubmitting}
                          disabled={isSubmitting || hasErrors || !values.name}
                        >
                          {t('Save profile')}
                        </SubmitButton>
                      </Flex>
                    </Grid.Col>
                  </Grid.Row>
                </Grid.Container>
              </Form>
            </ProfileCollapse.Panel>
          </ProfileCollapse>
        );
      }}
    </Formik>
  );
};

export default VNCCollapse;
